import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { products as productsApi, categories as categoriesApi, admin, getImageUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, Copy, Tag, DollarSign, Download, Upload, FileDown, ImagePlus } from 'lucide-react';

const AdminProducts = () => {
  usePageTitle('Manage Products');
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const fileInputRef = React.useRef(null);
  
  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkData, setBulkData] = useState({
    price: '',
    sale_price: '',
    is_on_sale: false,
    sale_type: '',
    sale_type_custom: '',
    is_new: false,
    is_coming_soon: false,
    category: '',
  });
  const [savingBulk, setSavingBulk] = useState(false);
  
  // Tag Dialog state
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState('');
  const [tagValue, setTagValue] = useState(true);
  const [savingTag, setSavingTag] = useState(false);
  
  // Available product tags
  const productTags = [
    { value: 'is_new', label: 'New', color: 'bg-green-500' },
    { value: 'is_best_seller', label: 'Best Seller', color: 'bg-yellow-500' },
    { value: 'is_top_seller', label: 'Top Seller', color: 'bg-purple-600' },
    { value: 'is_pre_order', label: 'Pre-Order', color: 'bg-blue-500' },
    { value: 'is_limited_edition', label: 'Limited Edition', color: 'bg-pink-600' },
    { value: 'is_popular', label: 'Popular', color: 'bg-indigo-500' },
    { value: 'is_coming_soon', label: 'Coming Soon', color: 'bg-blue-600' },
    { value: 'is_out_of_stock', label: 'Backorder', color: 'bg-orange-500' },
  ];
  
  // Quick Image Upload state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageProduct, setImageProduct] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [savingImage, setSavingImage] = useState(false);
  const [selectedImagesForDelete, setSelectedImagesForDelete] = useState(new Set());
  const [uploadingFile, setUploadingFile] = useState(false);
  const quickImageInputRef = React.useRef(null);

  // Barcode Import/Export state
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [importingBarcodes, setImportingBarcodes] = useState(false);
  const barcodeInputRef = React.useRef(null);
  
  // Sale type options
  const saleTypeOptions = [
    { value: 'black_friday', label: 'Black Friday' },
    { value: 'boxing_day', label: 'Boxing Day' },
    { value: 'australia_day', label: 'Australia Day' },
    { value: 'christmas', label: 'Christmas Sale' },
    { value: 'new_year', label: 'New Year Sale' },
    { value: 'eofy', label: 'EOFY Sale' },
    { value: 'end_of_season', label: 'End of Season' },
    { value: 'clearance', label: 'Clearance' },
    { value: 'flash_sale', label: 'Flash Sale' },
    { value: 'custom', label: 'Custom...' },
  ];
  
  // Pricing Dialog state (Sale + Stock Lot)
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [pricingProduct, setPricingProduct] = useState(null);
  const [pricingData, setPricingData] = useState({
    // Sale fields
    is_on_sale: false,
    sale_price: '',
    sale_type: '',
    sale_type_custom: '',
    // Stock Lot fields
    is_discontinued: false,
    is_stock_lot: false,
    stock_lot_price: ''
  });
  const [savingPricing, setSavingPricing] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    sku: '',
    price: '',
    sizing_type: 'unisex',
    images: ['']
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productsApi.getAll({ limit: 500 }),
        categoriesApi.getAll()
      ]);
      setProducts(productsRes.data.products);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        images: formData.images.filter(img => img.trim() !== '')
      };

      if (editingProduct) {
        await productsApi.update(editingProduct.id, productData);
        toast.success('Product updated successfully');
      } else {
        await productsApi.create(productData);
        toast.success('Product created successfully');
      }

      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      sku: product.sku,
      price: product.price.toString(),
      sizing_type: product.sizing_type,
      images: product.images.length > 0 ? product.images : ['']
    });
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await productsApi.delete(productId);
      toast.success('Product deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleClone = async (product) => {
    try {
      const response = await admin.cloneProduct(product.id);
      toast.success(`Product cloned! New SKU: ${response.data.sku}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone product');
    }
  };

  // Pricing Dialog Functions (Sale + Stock Lot)
  const openPricingDialog = (product) => {
    setPricingProduct(product);
    setPricingData({
      is_on_sale: product.is_on_sale || false,
      sale_price: product.sale_price?.toString() || '',
      sale_type: product.sale_type || '',
      sale_type_custom: product.sale_type_custom || '',
      is_discontinued: product.is_discontinued || false,
      is_stock_lot: product.is_stock_lot || false,
      stock_lot_price: product.stock_lot_price?.toString() || ''
    });
    setPricingDialogOpen(true);
  };

  const handlePricingSave = async () => {
    // Validation for Sale
    if (pricingData.is_on_sale && !pricingData.sale_price) {
      toast.error('Please set a sale price');
      return;
    }
    
    // Validation for Sale Type
    if (pricingData.is_on_sale && !pricingData.sale_type) {
      toast.error('Please select a sale type');
      return;
    }
    
    // Validation for Custom Sale Type
    if (pricingData.is_on_sale && pricingData.sale_type === 'custom' && !pricingData.sale_type_custom) {
      toast.error('Please enter a custom sale name');
      return;
    }
    
    // Validation for Stock Lot
    if (pricingData.is_stock_lot && !pricingData.is_discontinued) {
      toast.error('Product must be marked as discontinued to enable stock lot sale');
      return;
    }
    
    if (pricingData.is_stock_lot && !pricingData.stock_lot_price) {
      toast.error('Please set a stock lot price');
      return;
    }

    // Can't be on sale AND stock lot at the same time
    if (pricingData.is_on_sale && pricingData.is_stock_lot) {
      toast.error('Product cannot be on sale and stock lot at the same time');
      return;
    }

    setSavingPricing(true);
    try {
      await admin.updateProduct(pricingProduct.id, {
        is_on_sale: pricingData.is_on_sale,
        sale_price: pricingData.sale_price ? parseFloat(pricingData.sale_price) : null,
        sale_type: pricingData.is_on_sale ? pricingData.sale_type : null,
        sale_type_custom: pricingData.is_on_sale && pricingData.sale_type === 'custom' ? pricingData.sale_type_custom : null,
        is_discontinued: pricingData.is_discontinued,
        is_stock_lot: pricingData.is_stock_lot,
        stock_lot_price: pricingData.stock_lot_price ? parseFloat(pricingData.stock_lot_price) : null
      });
      
      let message = 'Pricing settings updated';
      if (pricingData.is_on_sale) {
        const saleLabel = pricingData.sale_type === 'custom' 
          ? pricingData.sale_type_custom 
          : saleTypeOptions.find(o => o.value === pricingData.sale_type)?.label;
        message = `Product is now on ${saleLabel}!`;
      }
      if (pricingData.is_stock_lot) message = 'Product configured as stock lot!';
      
      toast.success(message);
      setPricingDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing settings');
    } finally {
      setSavingPricing(false);
    }
  };

  const getProductTotalStock = (product) => {
    return product.inventory?.reduce((acc, inv) => acc + (inv.quantity || 0), 0) || 0;
  };

  const getProductOriginalValue = (product) => {
    const totalStock = getProductTotalStock(product);
    return totalStock * (product.price || 0);
  };

  // Handle bulk tag update
  const handleBulkTagUpdate = async () => {
    if (!selectedTag || selectedProducts.size === 0) {
      toast.error('Please select a tag and at least one product');
      return;
    }
    
    setSavingTag(true);
    try {
      await admin.bulkUpdateTags(Array.from(selectedProducts), selectedTag, tagValue);
      const tagLabel = productTags.find(t => t.value === selectedTag)?.label || selectedTag;
      toast.success(`${tagValue ? 'Added' : 'Removed'} "${tagLabel}" tag for ${selectedProducts.size} product(s)`);
      setTagDialogOpen(false);
      setSelectedProducts(new Set());
      setSelectedTag('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tags');
    } finally {
      setSavingTag(false);
    }
  };

  // Filter products by selected category
  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  // Group products by category
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const category = product.category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {});

  // Sort categories alphabetically
  const sortedCategories = Object.keys(groupedProducts).sort();

  // Bulk selection functions
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const selectCategoryProducts = (category) => {
    const categoryProducts = groupedProducts[category] || [];
    const categoryIds = categoryProducts.map(p => p.id);
    const allSelected = categoryIds.every(id => selectedProducts.has(id));
    
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        categoryIds.forEach(id => newSet.delete(id));
      } else {
        categoryIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const openBulkDialog = (action) => {
    setBulkAction(action);
    setBulkData({
      price: '',
      sale_price: '',
      is_on_sale: false,
      sale_type: '',
      sale_type_custom: '',
      is_new: false,
      is_coming_soon: false,
      category: '',
    });
    setBulkDialogOpen(true);
  };

  const handleBulkUpdate = async () => {
    if (selectedProducts.size === 0) {
      toast.error('No products selected');
      return;
    }

    setSavingBulk(true);
    try {
      const productIds = Array.from(selectedProducts);
      let updateData = {};

      switch (bulkAction) {
        case 'price':
          if (!bulkData.price) {
            toast.error('Please enter a price');
            setSavingBulk(false);
            return;
          }
          updateData = { price: parseFloat(bulkData.price) };
          break;
        case 'sale':
          if (!bulkData.sale_type) {
            toast.error('Please select a sale type');
            setSavingBulk(false);
            return;
          }
          if (!bulkData.sale_price) {
            toast.error('Please enter a sale price');
            setSavingBulk(false);
            return;
          }
          updateData = {
            is_on_sale: true,
            sale_price: parseFloat(bulkData.sale_price),
            sale_type: bulkData.sale_type,
            sale_type_custom: bulkData.sale_type === 'custom' ? bulkData.sale_type_custom : null,
          };
          break;
        case 'remove_sale':
          updateData = {
            is_on_sale: false,
            sale_price: null,
            sale_type: null,
            sale_type_custom: null,
          };
          break;
        case 'new_badge':
          updateData = { is_new: bulkData.is_new };
          break;
        case 'coming_soon':
          updateData = { is_coming_soon: bulkData.is_coming_soon };
          break;
        case 'category':
          if (!bulkData.category) {
            toast.error('Please select a category');
            setSavingBulk(false);
            return;
          }
          updateData = { category: bulkData.category };
          break;
        default:
          toast.error('Invalid action');
          setSavingBulk(false);
          return;
      }

      // Call bulk update API
      const response = await admin.bulkUpdateProducts(productIds, updateData);
      toast.success(`Updated ${response.data.updated} products`);
      setBulkDialogOpen(false);
      setSelectedProducts(new Set());
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update products');
    } finally {
      setSavingBulk(false);
    }
  };

  // Quick Image Upload functions
  const openImageDialog = (product) => {
    setImageProduct(product);
    setImageUrl('');
    setSelectedImagesForDelete(new Set());
    setImageDialogOpen(true);
  };

  const handleQuickImageSave = async () => {
    if (!imageUrl.trim()) {
      toast.error('Please enter an image URL');
      return;
    }

    setSavingImage(true);
    try {
      // Get current images and add the new one
      const currentImages = imageProduct.images || [];
      const updatedImages = [...currentImages, imageUrl.trim()];
      
      // Only send images field to avoid type conflicts
      await admin.updateProduct(imageProduct.id, {
        images: updatedImages
      });
      
      // Update local state
      setImageProduct(prev => ({
        ...prev,
        images: updatedImages
      }));
      
      toast.success('Image added successfully');
      setImageUrl('');
      loadData();
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add image');
    } finally {
      setSavingImage(false);
    }
  };

  const handleQuickFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFile(true);
    try {
      for (const file of files) {
        const response = await admin.uploadImage(imageProduct.id, file);
        if (response.data && response.data.images) {
          setImageProduct(prev => ({
            ...prev,
            images: response.data.images
          }));
        }
      }
      toast.success(`${files.length} image(s) uploaded successfully!`);
      loadData();
    } catch (error) {
      toast.error('Failed to upload image: ' + (error.message || 'Unknown error'));
    } finally {
      setUploadingFile(false);
      if (quickImageInputRef.current) {
        quickImageInputRef.current.value = '';
      }
    }
  };

  const toggleImageForDelete = (img) => {
    setSelectedImagesForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(img)) {
        newSet.delete(img);
      } else {
        newSet.add(img);
      }
      return newSet;
    });
  };

  const handleDeleteSelectedImages = async () => {
    if (selectedImagesForDelete.size === 0) {
      toast.error('Please select images to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedImagesForDelete.size} image(s)?`)) {
      return;
    }

    setSavingImage(true);
    try {
      const currentImages = imageProduct.images || [];
      const updatedImages = currentImages.filter(img => !selectedImagesForDelete.has(img));
      
      // Only send images field to avoid type conflicts
      await admin.updateProduct(imageProduct.id, {
        images: updatedImages
      });
      
      // Update local state
      setImageProduct(prev => ({
        ...prev,
        images: updatedImages
      }));
      
      setSelectedImagesForDelete(new Set());
      toast.success(`${selectedImagesForDelete.size} image(s) deleted successfully`);
      loadData();
    } catch (error) {
      console.error('Delete images error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete images');
    } finally {
      setSavingImage(false);
    }
  };

  // Barcode Export - Download template
  const handleExportBarcodeTemplate = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      window.open(`${API_URL}/api/admin/barcode-template?t=${timestamp}`, '_blank');
      toast.success('Barcode template download started');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Barcode Import - Upload CSV
  const handleBarcodeFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setImportingBarcodes(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await admin.importBarcodes(formData);
      
      toast.success(`Successfully imported ${response.data.imported_count} barcodes`);
      setBarcodeDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Barcode import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import barcodes');
    } finally {
      setImportingBarcodes(false);
      if (barcodeInputRef.current) {
        barcodeInputRef.current.value = '';
      }
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      sku: '',
      price: '',
      sizing_type: 'unisex',
      images: ['']
    });
  };

  const addImageField = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const updateImage = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Export products to CSV
  const handleExportProducts = async () => {
    try {
      const response = await admin.exportProductsCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Products exported successfully');
    } catch (error) {
      toast.error('Failed to export products');
    }
  };

  // Download product import template
  const handleDownloadTemplate = async () => {
    try {
      const response = await admin.downloadProductTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Import products from CSV
  const handleImportProducts = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    try {
      const response = await admin.importProductsCSV(file);
      const result = response.data;
      
      if (result.errors && result.errors.length > 0) {
        toast.warning(`Import complete with ${result.errors.length} errors. Created ${result.created}, Updated ${result.updated}.`);
      } else {
        toast.success(`Successfully imported: ${result.created} created, ${result.updated} updated`);
      }
      
      // Reload data
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import products');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 bg-white" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="admin-products">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Products</h1>
          <p className="text-[#64748B]">
            {selectedCategory === 'all' 
              ? `${products.length} products` 
              : `${filteredProducts.length} of ${products.length} products`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[180px]" data-testid="admin-category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.slug}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExportProducts} data-testid="export-products-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportProducts}
              accept=".csv"
              className="hidden"
              id="products-csv-upload"
            />
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              data-testid="import-products-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importing ? 'Importing...' : 'Import CSV'}
            </Button>
          </div>
          <Button 
            variant="outline"
            onClick={handleDownloadTemplate}
            data-testid="download-template-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button 
            variant="outline"
            onClick={() => setBarcodeDialogOpen(true)}
            data-testid="barcode-management-btn"
          >
            <Tag className="w-4 h-4 mr-2" />
            Barcodes
          </Button>
          <Button 
            className="bg-[#002FA7] hover:bg-[#0025a0]"
            onClick={() => navigate('/admin/products/new')}
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
          
          {/* Removed duplicate pricing dialog - correct one is at bottom of file */}
        </div>
      </div>

      {/* Bulk Action Toolbar */}
      {selectedProducts.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Checkbox 
              checked={selectedProducts.size === filteredProducts.length}
              onCheckedChange={toggleSelectAll}
            />
            <span className="font-medium text-blue-900">
              {selectedProducts.size} product{selectedProducts.size > 1 ? 's' : ''} selected
            </span>
            <Button variant="ghost" size="sm" onClick={() => setSelectedProducts(new Set())} className="text-blue-600">
              Clear Selection
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => openBulkDialog('price')}>
              <DollarSign className="w-4 h-4 mr-1" />
              Change Price
            </Button>
            <Button variant="outline" size="sm" onClick={() => openBulkDialog('sale')} className="text-red-600 border-red-200 hover:bg-red-50">
              <Tag className="w-4 h-4 mr-1" />
              Put on Sale
            </Button>
            <Button variant="outline" size="sm" onClick={() => openBulkDialog('remove_sale')}>
              Remove Sale
            </Button>
            <Button variant="outline" size="sm" onClick={() => openBulkDialog('new_badge')} className="text-green-600 border-green-200 hover:bg-green-50">
              NEW Badge
            </Button>
            <Button variant="outline" size="sm" onClick={() => openBulkDialog('coming_soon')} className="text-blue-600 border-blue-200 hover:bg-blue-50">
              Coming Soon
            </Button>
            <Button variant="outline" size="sm" onClick={() => setTagDialogOpen(true)} className="text-purple-600 border-purple-200 hover:bg-purple-50">
              <Tag className="w-4 h-4 mr-1" />
              Manage Tags
            </Button>
            <Button variant="outline" size="sm" onClick={() => openBulkDialog('category')}>
              Change Category
            </Button>
          </div>
        </div>
      )}

      {/* Products Table - Grouped by Category */}
      <div className="space-y-6">
        {sortedCategories.map((category) => (
          <div key={category} className="bg-white border border-[#E2E8F0] overflow-hidden">
            {/* Category Header */}
            <div className="bg-[#002FA7] text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={groupedProducts[category].every(p => selectedProducts.has(p.id))}
                  onCheckedChange={() => selectCategoryProducts(category)}
                  className="border-white data-[state=checked]:bg-white data-[state=checked]:text-[#002FA7]"
                />
                <Package className="w-5 h-5" />
                <span className="font-semibold capitalize">{category.replace(/-/g, ' ')}</span>
                <Badge variant="secondary" className="bg-white/20 text-white">
                  {groupedProducts[category].length} products
                </Badge>
              </div>
            </div>
            
            <table className="w-full">
              <thead className="bg-[#F8FAFC]">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B] w-10">
                    <Checkbox 
                      checked={groupedProducts[category].every(p => selectedProducts.has(p.id))}
                      onCheckedChange={() => selectCategoryProducts(category)}
                    />
                  </th>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">Product</th>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">SKU</th>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">Price</th>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">Stock</th>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {groupedProducts[category].map((product) => {
                  const totalStock = product.inventory?.reduce((acc, inv) => acc + inv.quantity, 0) || 0;
                  return (
                    <tr key={product.id} className={`hover:bg-[#F8FAFC] ${selectedProducts.has(product.id) ? 'bg-blue-50' : ''}`} data-testid={`product-row-${product.id}`}>
                      <td className="p-4">
                        <Checkbox 
                          checked={selectedProducts.has(product.id)}
                          onCheckedChange={() => toggleProductSelection(product.id)}
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-[#F8FAFC] overflow-hidden rounded relative">
                            <img
                              src={getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=100'}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                            {product.is_stock_lot && (
                              <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center">
                                <Tag className="w-4 h-4 text-orange-600" />
                              </div>
                            )}
                            {product.is_on_sale && !product.is_stock_lot && (
                              <div className="absolute top-0 right-0 bg-red-500 text-white text-[10px] px-1 font-bold">
                                {product.sale_type === 'custom' 
                                  ? (product.sale_type_custom || 'SALE').toUpperCase().slice(0, 10)
                                  : product.sale_type 
                                    ? product.sale_type.replace(/_/g, ' ').toUpperCase().slice(0, 10)
                                    : 'SALE'}
                              </div>
                            )}
                            {product.is_new && !product.is_stock_lot && !product.is_on_sale && (
                              <div className="absolute top-0 right-0 bg-green-500 text-white text-[10px] px-1 font-bold">
                                NEW
                              </div>
                            )}
                            {product.is_coming_soon && !product.is_stock_lot && !product.is_on_sale && !product.is_new && (
                              <div className="absolute top-0 right-0 bg-blue-500 text-white text-[10px] px-1 font-bold">
                                SOON
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-[#0F172A]">{product.name}</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              {product.color && (
                                <span className="text-xs text-[#64748B]">Color: {product.color}</span>
                              )}
                              {product.is_new && (
                                <Badge className="text-xs bg-green-500 text-white px-2 py-0.5 font-bold">NEW</Badge>
                              )}
                              {product.is_coming_soon && (
                                <Badge className="text-xs bg-blue-500 text-white px-2 py-0.5 font-bold">Coming Soon</Badge>
                              )}
                              {product.is_on_sale && !product.is_stock_lot && (
                                <Badge className="text-[10px] bg-red-100 text-red-700 px-1 py-0">
                                  {product.sale_type === 'custom' 
                                    ? product.sale_type_custom 
                                    : saleTypeOptions.find(o => o.value === product.sale_type)?.label || 'On Sale'}
                                </Badge>
                              )}
                              {product.is_discontinued && (
                                <Badge className="text-[10px] bg-gray-100 text-gray-700 px-1 py-0">Discontinued</Badge>
                              )}
                              {product.is_stock_lot && (
                                <Badge className="text-[10px] bg-orange-100 text-orange-700 px-1 py-0">Stock Lot</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm font-mono text-[#64748B]">{product.sku}</span>
                      </td>
                      <td className="p-4">
                        {product.is_stock_lot ? (
                          <div>
                            <span className="font-semibold text-green-600">${product.stock_lot_price?.toFixed(2) || '0.00'}</span>
                            <p className="text-xs text-[#64748B] line-through">${(totalStock * product.price).toFixed(2)}</p>
                          </div>
                        ) : product.is_on_sale ? (
                          <div>
                            <span className="font-semibold text-red-600">${product.sale_price?.toFixed(2) || '0.00'}</span>
                            <p className="text-xs text-[#64748B] line-through">${product.price.toFixed(2)}</p>
                          </div>
                        ) : (
                          <span className="font-semibold">${product.price.toFixed(2)}</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={totalStock < 50 ? 'text-orange-600' : 'text-green-600'}>
                          {totalStock} units
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/products/${product.id}`)}
                            title="Edit Product"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPricingDialog(product)}
                            title="Sale & Stock Lot Settings"
                            className={product.is_stock_lot || product.is_on_sale ? "text-orange-500 hover:text-orange-600" : "text-gray-500 hover:text-gray-600"}
                            data-testid={`pricing-product-${product.id}`}
                          >
                            <Tag className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleClone(product)}
                            title="Clone Product"
                            className="text-blue-500 hover:text-blue-600"
                            data-testid={`clone-product-${product.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openImageDialog(product)}
                            title="Quick Add Image"
                            className="text-green-500 hover:text-green-600"
                            data-testid={`image-product-${product.id}`}
                          >
                            <ImagePlus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(product.id)}
                            title="Delete"
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Sale & Stock Lot Settings Dialog */}
      <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-orange-500" />
              Sale & Stock Lot Settings
            </DialogTitle>
            <DialogDescription>
              Configure sale pricing or stock lot options for this product.
            </DialogDescription>
          </DialogHeader>

          {pricingProduct && (
            <div className="space-y-6 mt-4">
              {/* Product Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <img
                    src={pricingProduct.images?.[0] || 'https://images.unsplash.com/photo-1737094540182-d602f8a7dd1d'}
                    alt={pricingProduct.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div>
                    <h4 className="font-semibold">{pricingProduct.name}</h4>
                    <p className="text-sm text-gray-500">{pricingProduct.sku}</p>
                    <div className="flex gap-4 mt-1 text-sm">
                      <span>Unit Price: <strong>${pricingProduct.price?.toFixed(2)}</strong></span>
                      <span>Stock: <strong>{getProductTotalStock(pricingProduct)} units</strong></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* SALE SECTION */}
              <div className={`border rounded-lg p-4 ${pricingData.is_on_sale ? 'border-red-300 bg-red-50/30' : 'border-gray-200'}`}>
                <div className="flex items-center space-x-3 mb-3">
                  <Checkbox
                    id="pr_sale"
                    checked={pricingData.is_on_sale}
                    onCheckedChange={(v) => setPricingData(prev => ({ 
                      ...prev, 
                      is_on_sale: v,
                      // If enabling sale, disable stock lot
                      is_stock_lot: v ? false : prev.is_stock_lot,
                      is_discontinued: v ? false : prev.is_discontinued
                    }))}
                    disabled={pricingData.is_stock_lot}
                  />
                  <Label htmlFor="pr_sale" className={`cursor-pointer ${pricingData.is_stock_lot ? 'opacity-50' : ''}`}>
                    <span className="font-medium text-red-700">🏷️ Put on Sale</span>
                    <span className="text-sm text-gray-500 block">Product will be available at a discounted price</span>
                  </Label>
                </div>

                {pricingData.is_on_sale && (
                  <div className="ml-6 space-y-3">
                    <div>
                      <Label htmlFor="sale_type" className="text-sm font-medium">
                        Sale Type *
                      </Label>
                      <Select
                        value={pricingData.sale_type}
                        onValueChange={(v) => setPricingData(prev => ({ ...prev, sale_type: v }))}
                      >
                        <SelectTrigger className="w-full mt-1">
                          <SelectValue placeholder="Select sale type..." />
                        </SelectTrigger>
                        <SelectContent>
                          {saleTypeOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {pricingData.sale_type === 'custom' && (
                      <div>
                        <Label htmlFor="sale_type_custom" className="text-sm font-medium">
                          Custom Sale Name *
                        </Label>
                        <Input
                          id="sale_type_custom"
                          type="text"
                          value={pricingData.sale_type_custom}
                          onChange={(e) => setPricingData(prev => ({ ...prev, sale_type_custom: e.target.value }))}
                          placeholder="Enter custom sale name..."
                          className="mt-1"
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label htmlFor="sale_price" className="text-sm font-medium">
                        Sale Price *
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <Input
                          id="sale_price"
                          type="number"
                          step="0.01"
                          min="0"
                          value={pricingData.sale_price}
                          onChange={(e) => setPricingData(prev => ({ ...prev, sale_price: e.target.value }))}
                          placeholder="0.00"
                          className="w-32"
                        />
                        <span className="text-sm text-gray-500">per unit</span>
                      </div>
                    </div>
                    
                    {pricingData.sale_price && (
                      <div className="bg-white p-3 rounded border">
                        <div className="flex justify-between text-sm">
                          <span>Original:</span>
                          <span className="line-through text-gray-400">${pricingProduct.price?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Sale Price:</span>
                          <span className="font-semibold text-red-600">${parseFloat(pricingData.sale_price || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t mt-2">
                          <span>Discount:</span>
                          <span className="font-semibold text-red-600">
                            {pricingProduct.price > 0 
                              ? Math.round((1 - parseFloat(pricingData.sale_price || 0) / pricingProduct.price) * 100) 
                              : 0}% off
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* STOCK LOT SECTION */}
              <div className={`border rounded-lg p-4 ${pricingData.is_stock_lot ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200'}`}>
                <div className="flex items-center space-x-3 mb-3">
                  <Checkbox
                    id="pr_discontinued"
                    checked={pricingData.is_discontinued}
                    onCheckedChange={(v) => setPricingData(prev => ({ 
                      ...prev, 
                      is_discontinued: v,
                      is_stock_lot: v ? prev.is_stock_lot : false,
                      // If enabling discontinued, disable sale
                      is_on_sale: v ? false : prev.is_on_sale
                    }))}
                    disabled={pricingData.is_on_sale}
                  />
                  <Label htmlFor="pr_discontinued" className={`cursor-pointer ${pricingData.is_on_sale ? 'opacity-50' : ''}`}>
                    <span className="font-medium">Mark as Discontinued</span>
                    <span className="text-sm text-gray-500 block">Product will no longer be available for regular orders</span>
                  </Label>
                </div>

                <div className="flex items-center space-x-3 ml-6">
                  <Checkbox
                    id="pr_stocklot"
                    checked={pricingData.is_stock_lot}
                    onCheckedChange={(v) => setPricingData(prev => ({ ...prev, is_stock_lot: v }))}
                    disabled={!pricingData.is_discontinued}
                  />
                  <Label htmlFor="pr_stocklot" className={`cursor-pointer ${!pricingData.is_discontinued ? 'opacity-50' : ''}`}>
                    <span className="font-medium text-orange-700">📦 Sell as Stock Lot</span>
                    <span className="text-sm text-gray-500 block">Buyers must purchase entire inventory at once</span>
                  </Label>
                </div>

                {pricingData.is_stock_lot && (
                  <div className="ml-6 mt-3 space-y-3">
                    {/* Inventory Breakdown */}
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Inventory in Stock Lot:</Label>
                      <div className="flex flex-wrap gap-2">
                        {pricingProduct.inventory?.filter(inv => inv.quantity > 0).map(inv => (
                          <Badge key={inv.size} variant="secondary">
                            {inv.size}: {inv.quantity}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Total: <strong>{getProductTotalStock(pricingProduct)} units</strong> = <strong>${getProductOriginalValue(pricingProduct).toFixed(2)}</strong>
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="stocklot_price" className="text-sm font-medium">
                        Stock Lot Price *
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <Input
                          id="stocklot_price"
                          type="number"
                          step="0.01"
                          min="0"
                          value={pricingData.stock_lot_price}
                          onChange={(e) => setPricingData(prev => ({ ...prev, stock_lot_price: e.target.value }))}
                          placeholder="0.00"
                          className="w-32"
                        />
                        <span className="text-sm text-gray-500">for entire lot</span>
                      </div>
                    </div>
                    
                    {pricingData.stock_lot_price && (
                      <div className="bg-white p-3 rounded border">
                        <div className="flex justify-between text-sm">
                          <span>Original Value:</span>
                          <span className="line-through text-gray-400">${getProductOriginalValue(pricingProduct).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Stock Lot Price:</span>
                          <span className="font-semibold text-orange-600">${parseFloat(pricingData.stock_lot_price || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t mt-2">
                          <span>Discount:</span>
                          <span className="font-semibold text-orange-600">
                            {getProductOriginalValue(pricingProduct) > 0 
                              ? Math.round((1 - parseFloat(pricingData.stock_lot_price || 0) / getProductOriginalValue(pricingProduct)) * 100) 
                              : 0}% off
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setPricingDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handlePricingSave}
                  disabled={savingPricing}
                  className="bg-[#002FA7] hover:bg-[#0025a0]"
                >
                  {savingPricing ? 'Saving...' : 'Save Pricing Settings'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'price' && 'Change Price'}
              {bulkAction === 'sale' && 'Put on Sale'}
              {bulkAction === 'remove_sale' && 'Remove Sale'}
              {bulkAction === 'new_badge' && 'NEW Badge'}
              {bulkAction === 'coming_soon' && 'Coming Soon Badge'}
              {bulkAction === 'category' && 'Change Category'}
            </DialogTitle>
            <DialogDescription>
              Apply changes to {selectedProducts.size} selected product{selectedProducts.size > 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {bulkAction === 'price' && (
              <div>
                <Label htmlFor="bulk_price">New Price ($)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <Input
                    id="bulk_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={bulkData.price}
                    onChange={(e) => setBulkData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}

            {bulkAction === 'sale' && (
              <>
                <div>
                  <Label htmlFor="bulk_sale_type">Sale Type</Label>
                  <Select
                    value={bulkData.sale_type}
                    onValueChange={(v) => setBulkData(prev => ({ ...prev, sale_type: v }))}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Select sale type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {saleTypeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {bulkData.sale_type === 'custom' && (
                  <div>
                    <Label htmlFor="bulk_sale_custom">Custom Sale Name</Label>
                    <Input
                      id="bulk_sale_custom"
                      type="text"
                      value={bulkData.sale_type_custom}
                      onChange={(e) => setBulkData(prev => ({ ...prev, sale_type_custom: e.target.value }))}
                      placeholder="Enter custom sale name..."
                      className="mt-1"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="bulk_sale_price">Sale Price ($)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <Input
                      id="bulk_sale_price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={bulkData.sale_price}
                      onChange={(e) => setBulkData(prev => ({ ...prev, sale_price: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </>
            )}

            {bulkAction === 'remove_sale' && (
              <p className="text-gray-600">
                This will remove the sale status from all {selectedProducts.size} selected products.
              </p>
            )}

            {bulkAction === 'new_badge' && (
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="bulk_is_new"
                  checked={bulkData.is_new}
                  onCheckedChange={(v) => setBulkData(prev => ({ ...prev, is_new: v }))}
                />
                <Label htmlFor="bulk_is_new" className="cursor-pointer">
                  <span className="font-medium text-green-700">Mark as NEW</span>
                  <span className="text-sm text-gray-500 block">Show NEW badge on product cards</span>
                </Label>
              </div>
            )}

            {bulkAction === 'coming_soon' && (
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="bulk_coming_soon"
                  checked={bulkData.is_coming_soon}
                  onCheckedChange={(v) => setBulkData(prev => ({ ...prev, is_coming_soon: v }))}
                />
                <Label htmlFor="bulk_coming_soon" className="cursor-pointer">
                  <span className="font-medium text-blue-700">Mark as Coming Soon</span>
                  <span className="text-sm text-gray-500 block">Show COMING SOON badge</span>
                </Label>
              </div>
            )}

            {bulkAction === 'category' && (
              <div>
                <Label htmlFor="bulk_category">New Category</Label>
                <Select
                  value={bulkData.category}
                  onValueChange={(v) => setBulkData(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.slug}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpdate}
              disabled={savingBulk}
              className="bg-[#002FA7] hover:bg-[#0025a0]"
            >
              {savingBulk ? 'Updating...' : `Update ${selectedProducts.size} Products`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Image Upload Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImagePlus className="w-5 h-5 text-green-500" />
              Quick Add Image
            </DialogTitle>
            <DialogDescription>
              Add or remove images for {imageProduct?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Images with Checkboxes */}
            {imageProduct?.images?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-gray-500">Current Images ({imageProduct.images.length})</Label>
                  {selectedImagesForDelete.size > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteSelectedImages}
                      disabled={savingImage}
                      className="h-7 text-xs"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete Selected ({selectedImagesForDelete.size})
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-1">
                  {imageProduct.images.map((img, idx) => (
                    <div 
                      key={idx} 
                      className={`relative cursor-pointer rounded border-2 transition-all ${
                        selectedImagesForDelete.has(img) 
                          ? 'border-red-500 ring-2 ring-red-200' 
                          : 'border-transparent hover:border-gray-300'
                      }`}
                      onClick={() => toggleImageForDelete(img)}
                    >
                      <img 
                        src={getImageUrl(img) || img}
                        alt={`Current ${idx + 1}`}
                        className="w-full h-16 object-cover rounded"
                      />
                      <div className={`absolute top-1 left-1 w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedImagesForDelete.has(img)
                          ? 'bg-red-500 border-red-500 text-white'
                          : 'bg-white/80 border-gray-400'
                      }`}>
                        {selectedImagesForDelete.has(img) && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                          </svg>
                        )}
                      </div>
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-0.5 rounded-b">
                        {idx + 1}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">Click images to select for deletion</p>
              </div>
            )}
            
            {/* Upload File */}
            <div>
              <Label>Upload Image File</Label>
              <div className="mt-1">
                <input
                  type="file"
                  ref={quickImageInputRef}
                  onChange={handleQuickFileUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => quickImageInputRef.current?.click()}
                  disabled={uploadingFile}
                  className="w-full"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingFile ? 'Uploading...' : 'Choose Files'}
                </Button>
              </div>
            </div>

            {/* OR Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or add by URL</span>
              </div>
            </div>

            {/* URL Input */}
            <div>
              <Label htmlFor="image_url">Image URL</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="image_url"
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="flex-1"
                />
                <Button 
                  onClick={handleQuickImageSave}
                  disabled={savingImage || !imageUrl.trim()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingImage ? '...' : 'Add'}
                </Button>
              </div>
            </div>

            {imageUrl && (
              <div>
                <Label className="text-sm text-gray-500">Preview</Label>
                <img 
                  src={imageUrl}
                  alt="Preview"
                  className="w-20 h-20 object-cover rounded border mt-2"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setImageDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Barcode Import/Export Dialog */}
      <Dialog open={barcodeDialogOpen} onOpenChange={setBarcodeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-blue-500" />
              Barcode Management
            </DialogTitle>
            <DialogDescription>
              Export template, fill in barcodes, then import back
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Step 1: Export */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Export Template</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Download CSV with all products and sizes. Fill in the 'barcode' column.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={handleExportBarcodeTemplate}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Template
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 2: Import */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-bold text-sm">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Import Barcodes</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Upload the completed CSV file to update product barcodes.
                  </p>
                  <input
                    type="file"
                    ref={barcodeInputRef}
                    onChange={handleBarcodeFileSelect}
                    accept=".csv"
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => barcodeInputRef.current?.click()}
                    disabled={importingBarcodes}
                    className="gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    {importingBarcodes ? 'Importing...' : 'Upload CSV'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="text-xs text-gray-500 space-y-1">
              <p><strong>Tips:</strong></p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Open CSV in Excel or Google Sheets</li>
                <li>Fill the 'barcode' column for each size</li>
                <li>Save as CSV before uploading</li>
                <li>For Kids sizes: format column as Text to prevent date conversion</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setBarcodeDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Tags Dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-500" />
              Manage Product Tags
            </DialogTitle>
            <DialogDescription>
              Add or remove tags for {selectedProducts.size} selected product{selectedProducts.size > 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-3 block">Select Tag</Label>
              <div className="grid grid-cols-2 gap-2">
                {productTags.map((tag) => (
                  <button
                    key={tag.value}
                    onClick={() => setSelectedTag(tag.value)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      selectedTag === tag.value 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Badge className={`${tag.color} text-white text-xs mb-1`}>
                      {tag.label}
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">
                      {tag.value === 'is_new' && 'Mark as new arrival'}
                      {tag.value === 'is_best_seller' && 'Highlight best seller'}
                      {tag.value === 'is_top_seller' && 'Highlight top seller'}
                      {tag.value === 'is_pre_order' && 'Available for pre-order'}
                      {tag.value === 'is_limited_edition' && 'Limited availability'}
                      {tag.value === 'is_popular' && 'Trending product'}
                      {tag.value === 'is_coming_soon' && 'Coming soon'}
                      {tag.value === 'is_out_of_stock' && 'Backorder status'}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {selectedTag && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium">Action</span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={tagValue ? 'default' : 'outline'}
                    onClick={() => setTagValue(true)}
                    className={tagValue ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    Add Tag
                  </Button>
                  <Button
                    size="sm"
                    variant={!tagValue ? 'default' : 'outline'}
                    onClick={() => setTagValue(false)}
                    className={!tagValue ? 'bg-red-600 hover:bg-red-700' : ''}
                  >
                    Remove Tag
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setTagDialogOpen(false); setSelectedTag(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkTagUpdate}
              disabled={savingTag || !selectedTag}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingTag ? 'Updating...' : `Update ${selectedProducts.size} Products`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
